import classNames from 'classnames';
import React, { HTMLAttributes, ReactNode } from 'react';

import styles from './HelperText.module.css';
import { Icon } from '../Icon';

export type HelperTextVariant = 'assistive' | 'error' | 'success' | 'info' | 'alert';

export interface HelperTextProps extends Pick<
  HTMLAttributes<HTMLDivElement>,
  'children' | 'className' | 'style'
> {
  /**
   * Whenever you are using this component as a helper text for an input,
   * please provide an unique ID for this and use `aria-describedby` for the input
   * to refer the help text element.
   */
  id?: string;
  /**
   * Optional custom ARIA role for the helper text.
   */
  role?: string;

  /**
   * Whether to show the icon or not.
   * The visible icon depends on the `variant` prop.
   */
  showIcon?: boolean;
  /**
   * Which style to show for the helper text.
   */
  variant: HelperTextVariant;
}

const classNamesByVariant: Record<HelperTextVariant, string> = {
  assistive: styles.assistive,
  error: styles.error,
  success: styles.success,
  info: styles.info,
  alert: styles.alert,
};

const iconElementsByVariant: Record<HelperTextVariant, ReactNode> = {
  assistive: null,
  error: (
    <Icon
      className={styles.icon}
      name="status-error"
    />
  ),
  success: (
    <Icon
      className={styles.icon}
      name="status-positive"
    />
  ),
  info: (
    <Icon
      className={styles.icon}
      name="message-information"
    />
  ),
  alert: (
    <Icon
      className={styles.icon}
      name="status-critical"
    />
  ),
};

/**
 * The optional helper text is pertinent information that assists the user in correctly completing a field.
 * It is often used to explain the correct data format or inform of an input error.
 * @deprecated This component will be modified to only handle the helper part, the semantic variants will be removed in the next major release. For the semantic use, please use the new `MessageInputPopover` component instead.
 */
export function HelperText({
  children,
  className,
  id,
  role,
  showIcon = false,
  style,
  variant,
}: HelperTextProps) {
  const variantClassName = classNamesByVariant[variant];

  return (
    <div
      className={classNames(className, styles.container, variantClassName)}
      id={id}
      role={role}
      style={style}
    >
      {showIcon && iconElementsByVariant[variant]}
      <div className={styles.text}>{children}</div>
    </div>
  );
}
